import { mat4, ReadonlyVec3, vec2 } from 'gl-matrix';
import { gl } from '../../../Engine';
import { StructureSetObject } from '../../../data/structure/StructureSetObject';
import { TextureView } from '../../../view/TextureView';
import { PlaneView } from '../../../view/views/PlaneView';
import { ShaderProgram } from '../../utils/ShaderProgram';
import { UniformFloat } from '../../utils/uniforms/UniformFloat';
import { UniformMatrix } from '../../utils/uniforms/UniformMatrix';
import { UniformVec2 } from '../../utils/uniforms/UniformVec2';
import { UniformVec4 } from '../../utils/uniforms/UniformVec4';
import { Renderer } from '../Renderer';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';

/** Per-draw options for contour rendering. */
export interface ContourRenderOptions {
    /**
     * Color alpha (0..1). Default 1.0.
     * Passed as the alpha component of colorMask and used as an
     * RGB multiplier in the shader so the contour stays fully
     * opaque in the layer FBO but appears visually dimmer.
     */
    alpha?: number;

    /**
     * Interior fill alpha (0..1). Default 0.0 (no fill).
     * When > 0 the SDF interior is rendered with this alpha,
     * giving a translucent highlight of the region area.
     */
    fillAlpha?: number;
}

export class ContourRenderer implements Renderer {
    private readonly textureShader: ContourTextureShader;

    public constructor() {
        this.textureShader = new ContourTextureShader();
    }

    /**
     * Render all visible structures from the given structure sets
     * on a {@link PlaneView}.
     * @throws Error if the view type is not supported.
     */
    public render(
        view: TextureView,
        structureSets: StructureSetObject[],
        options?: ContourRenderOptions
    ): boolean {
        if (!(view instanceof PlaneView)) {
            throw new Error(
                'ContourRenderer on View of type ' +
                    `${view.constructor} currently not supported.`
            );
        }

        const alpha = options?.alpha ?? 1.0;
        const fillAlpha = options?.fillAlpha ?? 0.0;

        // Phase 1: Collect textures before setting up GL state.
        // Accessing roi.texture may trigger lazy SDF generation
        // (via SdfGenerator.renderSdf), which changes the active
        // GL program. Doing this before shader.start() avoids
        // rendering corruption from an unbound shader.
        const entries: {
            texture: ContourTexture;
            color: ReadonlyVec3;
            thickness: number;
            isSelected: boolean;
        }[] = [];

        for (const structureSet of structureSets) {
            for (const roi of structureSet.structures) {
                if (!roi.isVisible) {
                    continue;
                }
                const texture = roi.texture;
                if (roi.isEmpty || !texture) {
                    continue;
                }

                const isSelected = roi === structureSet.selectedStructure;
                let contourThickness = roi.contourThickness;
                if (isSelected) {
                    contourThickness *= 2;
                }

                entries.push({
                    texture,
                    color: roi.color,
                    thickness: contourThickness,
                    isSelected,
                });
            }
        }

        // Phase 2: Render with clean GL state — no SDF
        // generation can occur since all textures are resolved.
        gl.enable(gl.BLEND);
        gl.blendEquation(gl.FUNC_ADD);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        this.textureShader.start();
        this.textureShader.fillAlpha.loadFloat(fillAlpha);

        for (const entry of entries) {
            // Selected structure stays at full alpha; others are dimmed
            const entryAlpha = entry.isSelected ? 1.0 : alpha;
            this.drawContour(
                view,
                entry.texture,
                entry.color,
                entry.thickness,
                entryAlpha
            );
        }

        this.textureShader.stop();
        gl.disable(gl.BLEND);
        return true;
    }

    /**
     * Render a single contour texture on a PlaneView.
     * Sets up and tears down GL state internally so it
     * can be called as a standalone entry point.
     */
    public renderContourTexture(
        view: TextureView,
        texture: ContourTexture,
        color: ReadonlyVec3,
        contourThickness: number = 1.5,
        options?: ContourRenderOptions
    ): void {
        if (!(view instanceof PlaneView)) {
            return;
        }

        gl.enable(gl.BLEND);
        gl.blendEquation(gl.FUNC_ADD);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        this.textureShader.start();
        this.textureShader.fillAlpha.loadFloat(options?.fillAlpha ?? 0.0);
        this.drawContour(
            view,
            texture,
            color,
            contourThickness,
            options?.alpha ?? 1.0
        );
        this.textureShader.stop();
        gl.disable(gl.BLEND);
    }

    /**
     * Internal draw call for a single contour.
     * Assumes the shader is already started and blend is enabled.
     *
     * @param alpha Brightness multiplier passed via colorMask.a
     */
    private drawContour(
        view: PlaneView,
        texture: ContourTexture,
        color: ReadonlyVec3,
        contourThickness: number,
        alpha: number = 1.0
    ): void {
        const vao = texture.crossSection(view.projectionViewMatrix);
        if (!vao) {
            return;
        }

        // Inverse of projection-view, used to map direction vectors
        // from view NDC to patient space. Translation is irrelevant
        // because the shader multiplies with w = 0.0.
        const viewToPatient = mat4.invert(
            mat4.create(),
            view.projectionViewMatrix
        );

        // Contour line width: one pixel in NDC is 2/viewSize,
        // scaled by thickness.
        const pixelSize = vec2.fromValues(
            (2 * contourThickness) / view.size[0],
            (2 * contourThickness) / view.size[1]
        );
        this.textureShader.relativeDist.loadVec2(pixelSize);
        this.textureShader.colorMask.loadVec4(color, alpha);

        // Compose: view NDC direction → patient → texture UV [0,1]
        const viewToTextureUV = mat4.multiply(
            mat4.create(),
            texture.patientToImage,
            viewToPatient
        );
        this.textureShader.viewToTexture.loadMatrix(viewToTextureUV);

        texture.bindTexture();
        vao.bind(0, 1);
        gl.drawElements(gl.TRIANGLES, vao.indexCount, gl.UNSIGNED_INT, 0);
        vao.unbind(0, 1);
        vao.delete();
        texture.unbindTexture();
    }

    /**
     * Delete the shader allocated by this renderer.
     * Must be called when the renderer is no longer needed.
     */
    public delete(): void {
        this.textureShader.delete();
    }
}

/**
 * Shader that detects SDF boundary edges by sampling 8 neighbors
 * around each fragment in texture UV space. If any neighbor differs
 * from the center (inside vs outside the 0.5 threshold), the
 * fragment is on a contour edge and gets colored.
 *
 * colorMask.a carries the contour alpha — used as an RGB multiplier
 * so dimmed contours stay fully opaque in the layer FBO.
 *
 * fillAlpha controls interior rendering: when > 0 the SDF interior
 * is drawn with that alpha for a translucent region highlight.
 */
class ContourTextureShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es

    precision highp float;

    in vec2 position;
    in vec3 textureCoords;

    out vec3 pass_textureCoordinates;

    void main() {
        gl_Position = vec4(position, 0.0, 1.0);
        pass_textureCoordinates = textureCoords;
    }
    `;

    private static readonly FRAGMENT_CONTENT = `#version 300 es

    precision highp float;
    precision highp sampler3D;

    in vec3 pass_textureCoordinates;
    out vec4 outColor;

    uniform vec4 colorMask;
    uniform sampler3D sampler;
    uniform mat4 viewToTextureUV;
    uniform vec2 pixelSize;
    uniform float fillAlpha;

    const float DIAGONAL = inversesqrt(2.0);
    const float SDF_THRESHOLD = 0.5;

    // Sample the SDF at a neighbor offset (in view-space pixel units)
    // and return whether the sample is inside the structure.
    bool sampleNeighbor(float x, float y) {
        // Scale offset by pixel size to get view-space NDC delta
        vec2 viewDelta = vec2(x * pixelSize.x, y * pixelSize.y);

        // Transform direction vector from view NDC to texture UV space
        vec3 uvDelta = (viewToTextureUV * vec4(viewDelta, 0.0, 0.0)).xyz;
        vec3 samplePos = pass_textureCoordinates + uvDelta;

        // Treat out-of-bounds as outside the structure
        if (samplePos.x <= 0.0 || samplePos.x >= 1.0 ||
            samplePos.y <= 0.0 || samplePos.y >= 1.0 ||
            samplePos.z <= 0.0 || samplePos.z >= 1.0) {
            return false;
        }

        return texture(sampler, samplePos).r >= SDF_THRESHOLD;
    }

    void main() {
        float sdfValue = texture(sampler, pass_textureCoordinates).r;
        bool isInside = sdfValue >= SDF_THRESHOLD;

        // 8-neighbor edge detection: if any neighbor differs, this is an edge
        bool isEdge =
            sampleNeighbor(-DIAGONAL, -DIAGONAL) != isInside ||
            sampleNeighbor(     0.0,       -1.0) != isInside ||
            sampleNeighbor( DIAGONAL, -DIAGONAL) != isInside ||
            sampleNeighbor(    -1.0,        0.0) != isInside ||
            sampleNeighbor(     1.0,        0.0) != isInside ||
            sampleNeighbor(-DIAGONAL,  DIAGONAL) != isInside ||
            sampleNeighbor(     0.0,        1.0) != isInside ||
            sampleNeighbor( DIAGONAL,  DIAGONAL) != isInside;

        vec3 color = colorMask.rgb * colorMask.a;

        if (isEdge) {
            outColor = vec4(color, 1.0);
        } else if (isInside && fillAlpha > 0.0) {
            outColor = vec4(color, fillAlpha);
        } else {
            discard;
        }
    }`;

    public readonly colorMask: UniformVec4;
    public readonly viewToTexture: UniformMatrix;
    public readonly relativeDist: UniformVec2;
    public readonly fillAlpha: UniformFloat;

    constructor() {
        super(
            ContourTextureShader.VERTEX_CONTENT,
            ContourTextureShader.FRAGMENT_CONTENT,
            'position',
            'textureCoords'
        );
        this.colorMask = new UniformVec4('colorMask');
        this.viewToTexture = new UniformMatrix('viewToTextureUV');
        this.relativeDist = new UniformVec2('pixelSize');
        this.fillAlpha = new UniformFloat('fillAlpha');
        this.storeUniformLocations(
            this.colorMask,
            this.viewToTexture,
            this.relativeDist,
            this.fillAlpha
        );
    }
}
